<template>
  <div class="wechat-voice-container">
    <!-- 录音列表（模仿微信聊天气泡） -->
    <div class="voice-list">
      <div
        v-for="(item, index) in audioList"
        :key="index"
        class="voice-bubble-wrapper"
      >
        <!-- 左侧头像（占位） -->
        <div class="avatar">🐱</div>
        <!-- 语音气泡 -->
        <div
          class="voice-bubble"
          :class="{ playing: item.isPlaying }"
          @click="togglePlay(index)"
        >
          <div class="bubble-content">
            <!-- 播放/暂停图标 -->
            <span class="play-icon">{{ item.isPlaying ? "⏸" : "▶" }}</span>
            <!-- 模拟波形（动画） -->
            <span class="waveform">
              <i
                v-for="n in 8"
                :key="n"
                :style="{ animationDelay: n * 0.1 + 's' }"
              ></i>
            </span>
            <!-- 时长 -->
            <span class="duration">{{ item.duration }}″</span>
          </div>
          <!-- 播放进度条（仅播放时显示） -->
          <div class="progress-bar" v-if="item.isPlaying">
            <div
              class="progress-inner"
              :style="{ width: item.progress + '%' }"
            ></div>
          </div>
        </div>
        <!-- 文件信息与下载 -->
        <div class="file-info">
          <span>{{ (item.blob.size / 1024).toFixed(1) }} KB</span>
          <a :href="item.url" :download="`语音_${index + 1}.wav`" @click.stop
            >下载</a
          >
        </div>
      </div>
    </div>

    <!-- 底部按住说话按钮 -->
    <div class="record-btn-wrapper">
      <button
        class="record-btn"
        @touchstart.prevent="startRecord"
        @touchend.prevent="stopRecord"
        @touchcancel="stopRecord"
        @mousedown="startRecord"
        @mouseup="stopRecord"
        @mouseleave="stopRecord"
        :class="{ recording: isRecording }"
      >
        {{ isRecording ? "松开 结束" : "按住 说话" }}
      </button>
      <!-- 录音时的浮动提示（显示时长） -->
      <div v-if="isRecording" class="recording-hint">
        <div class="recording-timer">{{ recordDuration }}″</div>
        <div class="recording-wave">
          <span></span><span></span><span></span><span></span><span></span>
        </div>
        <div class="recording-tip">松开发送</div>
      </div>
    </div>
  </div>
</template>

<script>
import Recorder from "js-audio-recorder";

export default {
  name: "WechatVoiceRecorder",
  data() {
    return {
      recorder: null,
      isRecording: false,
      recordDuration: 0, // 当前录音秒数
      timer: null, // 计时器句柄
      audioList: [], // 存储 { blob, url, duration, isPlaying, progress }
    };
  },
  mounted() {
    this.recorder = new Recorder({
      sampleBits: 16,
      sampleRate: 16000,
      numChannels: 1,
    });
  },
  methods: {
    // ----- 录音控制 -----
    startRecord(e) {
      // 阻止默认事件（如页面滚动）
      e.preventDefault();
      if (this.isRecording) return;

      this.recorder
        .start()
        .then(() => {
          this.isRecording = true;
          this.recordDuration = 0;
          // 开始计时
          this.timer = setInterval(() => {
            this.recordDuration += 1;
          }, 1000);
          console.log("录音已开始");
        })
        .catch((err) => {
          console.error("麦克风授权失败:", err);
          alert("无法访问麦克风，请检查权限设置。");
        });
    },

    stopRecord(e) {
      e && e.preventDefault();
      if (!this.isRecording) return;

      // 停止录音
      this.recorder.stop();
      this.isRecording = false;
      clearInterval(this.timer);
      this.timer = null;
      console.log("录音已停止，时长:", this.recordDuration, "秒");

      // 获取音频数据
      const wavBlob = this.recorder.getWAVBlob();
      const audioUrl = URL.createObjectURL(wavBlob);
      const duration = this.recordDuration;

      // 打印语音文件（用户要求）
      console.log("语音文件", {
        blob: wavBlob,
        url: audioUrl,
        duration: duration,
        size: wavBlob.size,
      });

      // 添加到列表
      this.audioList.push({
        blob: wavBlob,
        url: audioUrl,
        duration: duration,
        isPlaying: false,
        progress: 0,
        audioElement: null, // 用于播放控制
      });

      // 重置计时
      this.recordDuration = 0;
    },

    // ----- 播放控制 -----
    togglePlay(index) {
      const item = this.audioList[index];
      if (item.isPlaying) {
        // 如果正在播放，则暂停
        if (item.audioElement) {
          item.audioElement.pause();
          item.audioElement.currentTime = 0;
        }
        item.isPlaying = false;
        item.progress = 0;
        this.$set(this.audioList, index, item);
        return;
      }

      // 停止其他正在播放的语音
      this.audioList.forEach((v, i) => {
        if (i !== index && v.isPlaying) {
          v.isPlaying = false;
          v.progress = 0;
          if (v.audioElement) {
            v.audioElement.pause();
            v.audioElement.currentTime = 0;
          }
          this.$set(this.audioList, i, v);
        }
      });

      // 开始播放当前语音
      const audio = new Audio(item.url);
      item.audioElement = audio;
      item.isPlaying = true;
      item.progress = 0;
      this.$set(this.audioList, index, item);

      audio.play();
      // 更新播放进度
      const updateProgress = () => {
        if (!item.isPlaying) return;
        const progress = (audio.currentTime / audio.duration) * 100;
        item.progress = Math.min(progress, 100);
        this.$set(this.audioList, index, item);
        if (progress < 100) {
          requestAnimationFrame(updateProgress);
        } else {
          // 播放结束
          item.isPlaying = false;
          item.progress = 0;
          this.$set(this.audioList, index, item);
        }
      };
      requestAnimationFrame(updateProgress);
    },

    // ----- 清理资源 -----
    beforeDestroy() {
      if (this.timer) clearInterval(this.timer);
      this.audioList.forEach((item) => {
        URL.revokeObjectURL(item.url);
        if (item.audioElement) {
          item.audioElement.pause();
          item.audioElement.src = "";
        }
      });
    },
  },
};
</script>

<style scoped>
/* ---------- 整体容器 ---------- */
.wechat-voice-container {
  display: flex;
  flex-direction: column;
  height: 100vh;
  background: #ededed;
  padding-bottom: 100px;
  box-sizing: border-box;
  position: relative;
  overflow-y: auto;
  user-select: none;
}

/* ---------- 语音列表（仿微信气泡） ---------- */
.voice-list {
  flex: 1;
  padding: 16px 12px;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.voice-bubble-wrapper {
  display: flex;
  align-items: center;
  gap: 12px;
}

.avatar {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: #c0c0c0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  flex-shrink: 0;
}

.voice-bubble {
  position: relative;
  max-width: 70%;
  min-width: 120px;
  background: #ffffff;
  border-radius: 12px 12px 12px 4px;
  padding: 12px 16px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
  cursor: pointer;
  transition: background 0.2s;
}

.voice-bubble.playing {
  background: #d3e3fd;
}

.bubble-content {
  display: flex;
  align-items: center;
  gap: 10px;
}

.play-icon {
  font-size: 18px;
  width: 24px;
  text-align: center;
}

/* 动态波形 */
.waveform {
  display: flex;
  align-items: center;
  gap: 3px;
  height: 20px;
}
.waveform i {
  display: block;
  width: 3px;
  height: 12px;
  background: #07c160;
  border-radius: 2px;
  animation: wave 0.8s ease-in-out infinite alternate;
}
.waveform i:nth-child(2) {
  height: 16px;
  animation-delay: 0.1s;
}
.waveform i:nth-child(3) {
  height: 10px;
  animation-delay: 0.2s;
}
.waveform i:nth-child(4) {
  height: 18px;
  animation-delay: 0.3s;
}
.waveform i:nth-child(5) {
  height: 12px;
  animation-delay: 0.4s;
}
.waveform i:nth-child(6) {
  height: 20px;
  animation-delay: 0.5s;
}
.waveform i:nth-child(7) {
  height: 14px;
  animation-delay: 0.6s;
}
.waveform i:nth-child(8) {
  height: 16px;
  animation-delay: 0.7s;
}

@keyframes wave {
  0% {
    transform: scaleY(0.4);
  }
  100% {
    transform: scaleY(1);
  }
}
/* 播放时波形变亮 */
.voice-bubble.playing .waveform i {
  background: #07c160;
}

.duration {
  font-size: 14px;
  color: #333;
  margin-left: auto;
  font-weight: 500;
}

/* 播放进度条 */
.progress-bar {
  width: 100%;
  height: 3px;
  background: #e0e0e0;
  border-radius: 2px;
  margin-top: 6px;
  overflow: hidden;
}
.progress-inner {
  height: 100%;
  background: #07c160;
  border-radius: 2px;
  transition: width 0.1s linear;
}

/* 文件信息 */
.file-info {
  display: flex;
  flex-direction: column;
  align-items: center;
  font-size: 12px;
  color: #888;
  gap: 4px;
  margin-left: 8px;
}
.file-info a {
  color: #07c160;
  text-decoration: none;
  font-weight: bold;
  padding: 4px 8px;
  border: 1px solid #07c160;
  border-radius: 12px;
  font-size: 11px;
}

/* ---------- 底部录音按钮 ---------- */
.record-btn-wrapper {
  position: fixed;
  bottom: 30px;
  left: 0;
  right: 0;
  display: flex;
  justify-content: center;
  padding: 0 20px;
  z-index: 100;
}

.record-btn {
  width: 100%;
  max-width: 360px;
  height: 60px;
  border: none;
  border-radius: 40px;
  background: #ffffff;
  color: #333;
  font-size: 18px;
  font-weight: 500;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  transition: all 0.2s;
  outline: none;
  touch-action: none;
}
.record-btn:active,
.record-btn.recording {
  background: #d9d9d9;
  transform: scale(0.96);
}
.record-btn.recording {
  background: #ff6b6b;
  color: #fff;
}

/* 录音浮动提示（时长+波纹） */
.recording-hint {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(4px);
  color: #fff;
  padding: 30px 40px;
  border-radius: 20px;
  text-align: center;
  z-index: 200;
  pointer-events: none;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  min-width: 150px;
}
.recording-timer {
  font-size: 48px;
  font-weight: bold;
  letter-spacing: 2px;
}
.recording-wave {
  display: flex;
  align-items: center;
  gap: 6px;
  height: 40px;
}
.recording-wave span {
  display: block;
  width: 6px;
  height: 20px;
  background: #07c160;
  border-radius: 4px;
  animation: wave 0.6s ease-in-out infinite alternate;
}
.recording-wave span:nth-child(2) {
  animation-delay: 0.1s;
  height: 30px;
}
.recording-wave span:nth-child(3) {
  animation-delay: 0.2s;
  height: 40px;
}
.recording-wave span:nth-child(4) {
  animation-delay: 0.3s;
  height: 30px;
}
.recording-wave span:nth-child(5) {
  animation-delay: 0.4s;
  height: 20px;
}
.recording-tip {
  font-size: 14px;
  color: #aaa;
  margin-top: 4px;
}
</style>



// WEBPACK FOOTER //
// src/components/DIYApplyCardSurface/VoiceRecording/VoiceRecording.vue